import React, { useEffect, useRef, useState } from 'react';
import api from '../../api/api';
import InventoryWarehouseSelect from '../../components/InventoryWarehouseSelect';
import ItemQuantityField from '../../components/ItemQuantityField';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchProducts } from '../../store/slices/productSlice';
import { getSelectableProductSearchItems } from '../../utils/selectableProducts';
import { escapeHtml, getInventoryPrintStyles, getWeightTypeLabel } from '../../utils/inventoryPrint';
import { openAndPrintHtml } from '../../utils/printDocument';
import { fetchSemiFinished } from '../../store/slices/semiFinishedSlice';
import WarehouseTransferAvailability, { WarehouseTransferAvailabilityData } from '../../components/WarehouseTransferAvailability';
import { isAxiosError } from 'axios';

type TransferItem = { key: number; productId: string; quantity: string };
const localDateTime = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
type WarehouseTransfer = {
  id: number;
  date: string;
  sourceWarehouseId: number;
  sourceWarehouseName: string;
  destinationWarehouseId: number;
  destinationWarehouseName: string;
  reason: string;
  notes: string;
  status: string;
  isHistoricalException?: boolean;
  items: Array<{ productId: number; productName: string; weightType: string; quantity: number }>;
  cancellationReason: string;
};

const InventoryWarehouseTransfersPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const products = useAppSelector(state => state.product.products);
  const semiFinished = useAppSelector(state => state.semiFinished.items);
  const nextKey = useRef(1);
  const newRow = (): TransferItem => ({ key: nextKey.current++, productId: '', quantity: '' });
  const [items, setItems] = useState<WarehouseTransfer[]>([]);
  const [selectedTransferIds, setSelectedTransferIds] = useState<number[]>([]);
  const [date, setDate] = useState(localDateTime);
  const [sourceWarehouseId, setSourceWarehouseId] = useState('');
  const [destinationWarehouseId, setDestinationWarehouseId] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<TransferItem[]>([newRow()]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [postingId, setPostingId] = useState<number | null>(null);
  const [tableFontSize, setTableFontSize] = useState(12);
  const [tableCellPadding, setTableCellPadding] = useState(7);
  const [preview, setPreview] = useState<WarehouseTransferAvailabilityData | null>(null);
  const [checking, setChecking] = useState(false);
  const [availability, setAvailability] = useState<{ id: number; data: WarehouseTransferAvailabilityData } | null>(null);
  const [checkingId, setCheckingId] = useState<number | null>(null);
  const previewGeneration = useRef(0);
  const availabilityGeneration = useRef(0);
  const productSearchItems = [
    ...getSelectableProductSearchItems(products, rows.map(row => row.productId))
      .map(item => ({ ...item, id: String(item.id) })),
    ...semiFinished.map(item => ({ id: `semi:${item.id}`, name: `${item.name} · Н/Ф` })),
  ];

  useEffect(() => {
    previewGeneration.current++;
    setPreview(null);
    setChecking(false);
  }, [rows, sourceWarehouseId, destinationWarehouseId, date]);

  useEffect(() => () => {
    previewGeneration.current++;
    availabilityGeneration.current++;
  }, []);

  const requestItems = () => rows.map(row => ({
    productId: row.productId.startsWith('semi:') ? 0 : Number(row.productId),
    semiFinishedProductId: row.productId.startsWith('semi:') ? Number(row.productId.slice(5)) : null,
    quantity: Number(row.quantity),
  }));
  const requestPayload = () => ({
    date: new Date(date).toISOString(),
    sourceWarehouseId: Number(sourceWarehouseId),
    destinationWarehouseId: Number(destinationWarehouseId),
    reason: reason.trim(), notes, items: requestItems(),
  });
  const errorMessage = (requestError: unknown, fallback: string) =>
    isAxiosError<{ message?: string }>(requestError) ? requestError.response?.data?.message || fallback : fallback;

  const checkPreview = async () => {
    if (!date || !sourceWarehouseId || !destinationWarehouseId || rows.some(row =>
      !row.productId || !Number.isFinite(Number(row.quantity)) || Number(row.quantity) <= 0)) {
      setError('Оберіть склади, дату, продукти або Н/Ф і додатні кількості.');
      return;
    }
    const generation = ++previewGeneration.current;
    setChecking(true);
    setPreview(null);
    setError('');
    try {
      const response = await api.post<WarehouseTransferAvailabilityData>('/inventorywarehousetransfers/preview', requestPayload());
      if (generation === previewGeneration.current) setPreview(response.data);
    } catch (requestError: unknown) {
      if (generation === previewGeneration.current)
        setError(errorMessage(requestError, 'Не вдалося перевірити рецептуру та нестачу.'));
    } finally {
      if (generation === previewGeneration.current) setChecking(false);
    }
  };

  const checkAvailability = async (id: number) => {
    const generation = ++availabilityGeneration.current;
    setCheckingId(id);
    setAvailability(null);
    setError('');
    try {
      const response = await api.get<WarehouseTransferAvailabilityData>(`/inventorywarehousetransfers/${id}/availability`);
      if (generation === availabilityGeneration.current) setAvailability({ id, data: response.data });
    } catch (requestError: unknown) {
      if (generation === availabilityGeneration.current)
        setError(errorMessage(requestError, 'Не вдалося перевірити нестачу переміщення.'));
    } finally {
      if (generation === availabilityGeneration.current) setCheckingId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get<WarehouseTransfer[]>('/inventorywarehousetransfers');
      setItems(response.data);
      setError('');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Не вдалося завантажити переміщення між складами.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchSemiFinished());
    void load();
  }, [dispatch]);

  const updateRow = (key: number, patch: Partial<TransferItem>) =>
    setRows(current => current.map(row => row.key === key ? { ...row, ...patch } : row));

  const createTransfer = async (event: React.FormEvent) => {
    event.preventDefault();
    if (sourceWarehouseId === destinationWarehouseId) {
      setError('Оберіть різні склади відправлення та отримання.');
      return;
    }
    if (!reason.trim()) {
      setError('Вкажіть причину фізичного переміщення.');
      return;
    }
    const transferItems = requestItems();
    if (!transferItems.length || transferItems.some(item =>
      (!Number.isInteger(item.productId) || item.productId < 0 ||
        (item.productId === 0 && (!Number.isInteger(item.semiFinishedProductId) || !item.semiFinishedProductId))) ||
      !Number.isFinite(item.quantity) || item.quantity <= 0)) {
      setError('Для кожного рядка оберіть продукт або Н/Ф і вкажіть кількість більше 0.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const response = await api.post<WarehouseTransfer>('/inventorywarehousetransfers', requestPayload());
      setRows([newRow()]);
      setReason('');
      setNotes('');
      await load();
      setMessage(response.data.status === 'Draft'
        ? 'Переміщення збережено як непроведене: недостатній залишок. Залишки обох складів не змінено. Документ можна надрукувати й провести окремо після перевірки запасу на дату документа.'
        : 'Переміщення проведено.');
      if (response.data.status === 'Draft') await checkAvailability(response.data.id);
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Не вдалося провести переміщення.');
    } finally {
      setSaving(false);
    }
  };

  const postTransfer = async (transferId: number) => {
    setPostingId(transferId);
    setError('');
    setMessage('');
    try {
      await api.post(`/inventorywarehousetransfers/${transferId}/post`);
      availabilityGeneration.current++;
      setAvailability(null);
      await load();
      setMessage('Переміщення проведено після перевірки залишку на дату документа.');
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Не вдалося підтвердити проведення. Оновіть історію перед повторною дією.');
    } finally {
      setPostingId(null);
    }
  };

  const statusLabel = (status: string) => status === 'Draft'
    ? 'Непроведене списання · очікує проведення; залишки не змінено'
    : status === 'Cancelled' ? 'Скасовано' : status === 'Posted' ? 'Проведено' : status;

  const buildTransferPrintSheet = (transfer: WarehouseTransfer) => {
    const details = transfer.items.map(item => `<tr>
      <td>${escapeHtml(item.productName)}</td>
      <td style="text-align:center">${escapeHtml(getWeightTypeLabel(item.weightType))}</td>
      <td style="text-align:right">${item.quantity.toFixed(3)}</td>
    </tr>`).join('');
    return `<section class="receipt-sheet transfer-sheet"><div class="receipt-top">
      <h2>Переміщення між складами #${transfer.id}</h2>
      <div class="receipt-meta-grid">
      <div><strong>Дата й час:</strong> ${escapeHtml(new Date(transfer.date).toLocaleString('uk-UA'))}</div>
      <div><strong>Статус:</strong> ${escapeHtml(statusLabel(transfer.status))}</div>
      <div><strong>Склад відправлення:</strong> ${escapeHtml(transfer.sourceWarehouseName)}</div>
      <div><strong>Склад отримання:</strong> ${escapeHtml(transfer.destinationWarehouseName)}</div>
      <div><strong>Причина:</strong> ${escapeHtml(transfer.reason)}</div>
      ${transfer.notes ? `<div><strong>Примітки:</strong> ${escapeHtml(transfer.notes)}</div>` : ''}
      ${transfer.status === 'Cancelled' ? `<div><strong>Причина скасування:</strong> ${escapeHtml(transfer.cancellationReason)}</div>` : ''}
      </div></div><table><thead><tr><th>Продукт</th><th>Од.</th><th>К-сть</th></tr></thead>
      <tbody>${details}</tbody></table><div class="receipt-bottom">
      <div>Передав(ла): ____________________</div><div>Отримав(ла): ____________________</div>
      </div></section>`;
  };

  const printTransfers = (transfers: WarehouseTransfer[]) => {
    if (transfers.length === 0) return;
    const title = transfers.length === 1
      ? `Переміщення між складами #${transfers[0].id}`
      : `Переміщення між складами (${transfers.length})`;
    void openAndPrintHtml(`<html><head><title>${escapeHtml(title)}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;color:#2c3e50}${getInventoryPrintStyles()}
      .pdf-receipt-root th,.pdf-receipt-root td{font-size:${tableFontSize}px;padding:${tableCellPadding}px}
      .pdf-receipt-root table{table-layout:fixed}.pdf-receipt-root td{overflow-wrap:anywhere}
      thead{display:table-header-group}tr{break-inside:avoid}
      @media print{.transfer-sheet:not(:last-child){break-after:page;page-break-after:always}}</style></head><body>
      <div class="pdf-receipt-root">${transfers.map(buildTransferPrintSheet).join('')}</div></body></html>`, {
      windowFeatures: 'width=1000,height=700',
      onPopupBlocked: () => setError('Браузер заблокував вікно друку. Дозвольте спливаючі вікна для CRM і повторіть друк.'),
      onError: () => setError('Не вдалося підготувати переміщення до друку. Повторіть спробу.'),
    });
  };

  const printTransfer = (transfer: WarehouseTransfer) => printTransfers([transfer]);
  const toggleTransferSelection = (transferId: number) => setSelectedTransferIds(current =>
    current.includes(transferId) ? current.filter(id => id !== transferId) : [...current, transferId]);
  const selectAllTransfers = () => setSelectedTransferIds(items.map(transfer => transfer.id));
  const printSelectedTransfers = () => {
    const transfers = items
      .filter(transfer => selectedTransferIds.includes(transfer.id))
      .sort((left, right) => new Date(left.date).getTime() - new Date(right.date).getTime() || left.id - right.id);
    if (transfers.length === 0) return;

    const rows = transfers.flatMap(transfer => {
      const route = `${transfer.sourceWarehouseName} → ${transfer.destinationWarehouseName}`;
      const dateLabel = new Date(transfer.date).toLocaleString('uk-UA');
      return (transfer.items.length ? transfer.items : [null]).map(item => `
        <tr>
          <td>${transfer.id}</td>
          <td>${escapeHtml(dateLabel)}</td>
          <td>${escapeHtml(route)}</td>
          <td>${item ? escapeHtml(item.productName) : 'Позиції відсутні'}</td>
          <td style="text-align:center">${item ? escapeHtml(getWeightTypeLabel(item.weightType)) : '—'}</td>
          <td style="text-align:right">${item ? item.quantity.toFixed(3) : '—'}</td>
        </tr>`);
    }).join('');
    const firstDate = new Date(transfers[0].date).toLocaleDateString('uk-UA');
    const lastDate = new Date(transfers[transfers.length - 1].date).toLocaleDateString('uk-UA');
    const title = `Обрані переміщення між складами (${transfers.length})`;

    void openAndPrintHtml(`<html><head><title>${escapeHtml(title)}</title>
      <style>@page{size:A4 landscape;margin:10mm}
      body{font-family:Arial,sans-serif;padding:14px;color:#2c3e50}${getInventoryPrintStyles()}
      .pdf-receipt-root table{table-layout:fixed;width:100%}
      .pdf-receipt-root col.transfer-number{width:5%}
      .pdf-receipt-root col.transfer-date{width:17%}
      .pdf-receipt-root col.transfer-route{width:23%}
      .pdf-receipt-root col.transfer-product{width:39%}
      .pdf-receipt-root col.transfer-unit{width:7%}
      .pdf-receipt-root col.transfer-quantity{width:9%}
      .pdf-receipt-root th,.pdf-receipt-root td{font-size:${tableFontSize}px;padding:${tableCellPadding}px;vertical-align:top}
      .pdf-receipt-root td{overflow-wrap:anywhere;word-break:break-word}
      .pdf-receipt-root th{white-space:nowrap}thead{display:table-header-group}tr{break-inside:avoid}</style>
      </head><body><div class="pdf-receipt-root"><section class="receipt-sheet transfer-list-sheet">
        <div class="receipt-top"><div class="receipt-title-row">
          <h2>${escapeHtml(title)}</h2><span class="page-index">Період: ${escapeHtml(firstDate)} — ${escapeHtml(lastDate)}</span>
        </div></div>
        <table><colgroup><col class="transfer-number"><col class="transfer-date"><col class="transfer-route">
          <col class="transfer-product"><col class="transfer-unit"><col class="transfer-quantity"></colgroup>
          <thead><tr><th>№</th><th>Дата й час</th><th>Напрям переміщення</th>
          <th>Продукт</th><th>Од.</th><th>К-сть</th></tr></thead>
          <tbody>${rows}</tbody></table>
      </section></div></body></html>`, {
      windowFeatures: 'width=1200,height=800',
      onPopupBlocked: () => setError('Браузер заблокував вікно друку. Дозвольте спливаючі вікна для CRM і повторіть друк.'),
      onError: () => setError('Не вдалося підготувати обрані переміщення до друку. Повторіть спробу.'),
    });
  };

  const cancelTransfer = async (transferId: number) => {
    const cancellationReason = window.prompt('Причина скасування фізичного переміщення:')?.trim();
    if (!cancellationReason) return;
    setError('');
    setMessage('');
    try {
      await api.post(`/inventorywarehousetransfers/${transferId}/cancel`, { reason: cancellationReason });
      availabilityGeneration.current++;
      setAvailability(null);
      await load();
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || 'Не вдалося скасувати переміщення.');
    }
  };

  return (
    <main className="page-shell page-shell--wide" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ color: 'var(--crm-text)' }}>Переміщення між складами</h2>
      <p style={{ color: 'var(--crm-text-muted)' }}>
        Фіксуйте фактичне переміщення запасів між складами. Об’єднання підрозділів не перерозподіляє залишки автоматично.
      </p>
      {error && <div role="alert" style={{ color: 'var(--crm-danger)', marginBottom: 12 }}>{error}</div>}
      {message && <p role="status">{message}</p>}
      <p>Якщо продукту недостатньо, документ збережеться як непроведений. Він не змінює залишки й не резервує запас до окремого проведення.</p>
      <p>Н/Ф розкладається на інгредієнти за рецептурою. Між складами рухаються продукти, а не готовий Н/Ф; підроздільні залишки Н/Ф не змінюються. Збережений склад інгредієнтів надалі не перераховується.</p>
      <form onSubmit={createTransfer} style={{ background: 'var(--crm-surface)', padding: 16, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', marginBottom: 20 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'end' }}>
          <label>Дата й час фізичного переміщення<input type="datetime-local" required disabled={saving} value={date} onChange={event => setDate(event.target.value)} style={{ display: 'block', minHeight: 44, fontSize: 16 }} /></label>
          <InventoryWarehouseSelect label="Склад відправлення" value={sourceWarehouseId} onChange={setSourceWarehouseId} disabled={saving} />
          <InventoryWarehouseSelect label="Склад отримання" value={destinationWarehouseId} onChange={setDestinationWarehouseId} disabled={saving} />
          <label style={{ flex: '1 1 220px' }}>Причина
            <input required disabled={saving} value={reason} onChange={event => setReason(event.target.value)} style={{ display: 'block', width: '100%', minHeight: 44, fontSize: 16 }} placeholder="Фактичне переміщення запасу" />
          </label>
          <label style={{ flex: '1 1 220px' }}>Примітки
            <input disabled={saving} value={notes} onChange={event => setNotes(event.target.value)} style={{ display: 'block', width: '100%', minHeight: 44, fontSize: 16 }} />
          </label>
        </div>
        <div style={{ display: 'grid', gap: 10, margin: '16px 0' }}>
          {rows.map((row, index) => (
            <div key={row.key} className="crm-position-card" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <ItemQuantityField
                items={productSearchItems}
                value={row.productId}
                onChange={productId => updateRow(row.key, { productId })}
                quantity={row.quantity}
                onQuantityChange={quantity => updateRow(row.key, { quantity })}
                label={`Продукт ${index + 1}`}
                placeholder="Пошук продукту або Н/Ф..."
                quantityLabel="Кількість"
                unit={getWeightTypeLabel(row.productId.startsWith('semi:')
                  ? semiFinished.find(item => `semi:${item.id}` === row.productId)?.weightType
                  : products.find(product => String(product.id) === row.productId)?.weightType)}
                disabled={saving}
                min={0.001}
                step={0.001}
              />
              <button type="button" disabled={saving || rows.length <= 1} onClick={() => setRows(current => current.filter(item => item.key !== row.key))}
                style={{ minWidth: 44, minHeight: 44 }} aria-label={`Видалити позицію ${index + 1}`}>Видалити</button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" disabled={saving} onClick={() => setRows(current => [...current, newRow()])} style={{ minHeight: 44 }}>+ Додати продукт або Н/Ф</button>
          <button type="button" disabled={saving || checking} onClick={() => void checkPreview()} style={{ minHeight: 44 }}>
            {checking ? 'Перевірка...' : 'Перевірити інгредієнти та нестачу'}
          </button>
          <button type="submit" disabled={saving} style={{ minHeight: 44 }}>{saving ? 'Збереження...' : 'Зберегти переміщення'}</button>
        </div>
        {preview && <WarehouseTransferAvailability data={preview} />}
      </form>

      <h3>Історія переміщень</h3>
      <button type="button" disabled={loading} onClick={() => void load()} style={{ minHeight: 44 }}>Оновити історію</button>
      {!loading && items.length > 0 && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
        <span>Вибрано для друку: {selectedTransferIds.filter(id => items.some(transfer => transfer.id === id)).length}</span>
        <button type="button" onClick={selectAllTransfers} style={{ minHeight: 36 }}>Вибрати всі</button>
        <button type="button" onClick={() => setSelectedTransferIds([])} disabled={selectedTransferIds.length === 0} style={{ minHeight: 36 }}>Зняти вибір</button>
        <button type="button" onClick={printSelectedTransfers} disabled={!selectedTransferIds.some(id => items.some(transfer => transfer.id === id))} style={{ minHeight: 36 }}>
          🖨 Друк обраних ({selectedTransferIds.filter(id => items.some(transfer => transfer.id === id)).length})
        </button>
      </div>}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <label>Розмір шрифту друку
          <input type="number" min={8} max={20} value={tableFontSize}
            onChange={event => setTableFontSize(Math.min(20, Math.max(8, Number(event.target.value) || 12)))}
            style={{ display: 'block', minHeight: 44, fontSize: 16, width: 100 }} />
        </label>
        <label>Відступи клітинок друку
          <input type="number" min={2} max={16} value={tableCellPadding}
            onChange={event => setTableCellPadding(Math.min(16, Math.max(2, Number(event.target.value) || 7)))}
            style={{ display: 'block', minHeight: 44, fontSize: 16, width: 100 }} />
        </label>
      </div>
      {loading ? <p>Завантаження...</p> : items.length === 0 ? <p>Переміщень ще немає.</p> : (
        <div style={{ display: 'grid', gap: 6 }}>
          {items.map(transfer => (
            <article key={transfer.id} style={{ background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 420px', minWidth: 0 }}>
                  <input type="checkbox" aria-label={`Вибрати переміщення #${transfer.id} для друку`}
                    checked={selectedTransferIds.includes(transfer.id)}
                    onChange={() => toggleTransferSelection(transfer.id)}
                    style={{ width: 20, height: 20, flex: '0 0 auto' }} />
                  <strong style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    #{transfer.id} · {new Date(transfer.date).toLocaleString('uk-UA')} · {transfer.sourceWarehouseName} → {transfer.destinationWarehouseName}
                  </strong>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => printTransfer(transfer)} style={{ minHeight: 36 }}>Друк</button>
                  {transfer.status === 'Draft' && <button type="button" disabled={postingId !== null}
                    onClick={() => void postTransfer(transfer.id)} style={{ minHeight: 36 }}>
                    {postingId === transfer.id ? 'Проведення...' : 'Провести'}
                  </button>}
                  {transfer.status === 'Draft' && <button type="button" disabled={checkingId !== null || postingId !== null}
                    onClick={() => void checkAvailability(transfer.id)} style={{ minHeight: 36 }}>
                    {checkingId === transfer.id ? 'Перевірка...' : 'Перевірити нестачу / додати продукт'}
                  </button>}
                  {!transfer.isHistoricalException && (transfer.status === 'Posted' || transfer.status === 'Draft') &&
                    <button type="button" disabled={postingId !== null} onClick={() => void cancelTransfer(transfer.id)} style={{ minHeight: 36 }}>Скасувати</button>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline', marginTop: 4, fontSize: 13 }}>
                <strong>{statusLabel(transfer.status)}</strong>
                {transfer.isHistoricalException && <span>Історичний виняток — тільки перегляд і друк.</span>}
                <span>{transfer.reason}</span>
                {transfer.notes && <span style={{ color: 'var(--crm-text-muted)' }}>{transfer.notes}</span>}
                {transfer.status === 'Cancelled' && <span>Скасовано: {transfer.cancellationReason}</span>}
                <details>
                  <summary style={{ cursor: 'pointer' }}>Позиції ({transfer.items.length})</summary>
                  <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    {transfer.items.map(item => <li key={item.productId}>{item.productName}: {item.quantity} {item.weightType}</li>)}
                  </ul>
                </details>
              </div>
              {transfer.status === 'Draft' && availability?.id === transfer.id &&
                <WarehouseTransferAvailability data={availability.data} transferId={transfer.id} />}
            </article>
          ))}
        </div>
      )}
    </main>
  );
};

export default InventoryWarehouseTransfersPage;
